import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { siteData } from "../data/mock";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const RAW_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const BACKEND_URL = RAW_BACKEND_URL.replace(/\/+$/, ""); // strip trailing slash
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "";

const RadioTile = ({ label, checked, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex items-center gap-3 px-4 py-3 rounded-lg border text-left transition-colors w-full ${
      checked
        ? "border-[#5EC7F5]/60 bg-[#5EC7F5]/10"
        : "hairline bg-[#0b1220]/60 hover:border-[#5EC7F5]/25"
    }`}
  >
    <span
      className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
        checked ? "border-[#5EC7F5]" : "border-slate-500"
      }`}
    >
      {checked && <span className="w-2 h-2 rounded-full bg-[#5EC7F5]" />}
    </span>
    <span className={`text-[13.5px] ${checked ? "text-white" : "text-slate-300"}`}>
      {label}
    </span>
  </button>
);

const FieldLabel = ({ children }) => (
  <label className="text-[11px] tracking-[0.24em] uppercase text-slate-400">
    {children} <span className="text-[#5EC7F5]">*</span>
  </label>
);

const RadioGroup = ({ options, value, onChange, cols = 2 }) => (
  <div className={`grid ${cols === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2"} gap-3 mt-3`}>
    {options.map((opt) => (
      <RadioTile
        key={opt}
        label={opt}
        checked={value === opt}
        onClick={() => onChange(opt)}
      />
    ))}
  </div>
);

const Register = () => {
  const { register } = siteData;
  const { toast } = useToast();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    status: "",
    ai: "",
    reason: "",
    interest: "",
    agree: false,
  });

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    const missing = ["name", "email", "phone", "city", "status", "ai", "reason", "interest"].find((k) => !form[k]);
    if (missing || !form.agree) {
      toast({
        title: "Please complete all fields",
        description: "Fill in the required fields and accept communications.",
      });
      return;
    }
    if (!API) {
      toast({
        title: "Configuration error",
        description:
          "Backend URL is not set. Set REACT_APP_BACKEND_URL in .env.production and rebuild before deploying.",
      });
      // eslint-disable-next-line no-console
      console.error(
        "REACT_APP_BACKEND_URL is missing. The form has no backend to post to. " +
          "Create /app/frontend/.env.production with REACT_APP_BACKEND_URL=<your api url> and run `yarn build`."
      );
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/register`, form, { timeout: 30000 });
      navigate("/thank-you", {
        state: { name: form.name, email: form.email },
        replace: false,
      });
    } catch (err) {
      let detail =
        err?.response?.data?.detail ||
        (err?.code === "ERR_NETWORK"
          ? `Cannot reach ${API}. Check that the backend URL is correct and CORS is open.`
          : "Please try again in a moment.");
      if (typeof detail !== "string") detail = "Please check your details and try again.";
      toast({
        title: "Could not submit registration",
        description: detail,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="register" className="py-16 sm:py-20 lg:py-32 relative">
      <div className="max-w-[1080px] mx-auto px-5 sm:px-6 lg:px-10">
        <div className="text-center">
          <div className="flex items-center justify-center gap-3">
            <span className="eyebrow-line" />
            <span className="text-[11px] tracking-[0.32em] uppercase text-[#5EC7F5]">
              {register.eyebrow}
            </span>
            <span className="eyebrow-line" />
          </div>
          <h2 className="font-display text-[32px] sm:text-[44px] md:text-[52px] lg:text-[60px] leading-[1.02] sm:leading-[0.98] mt-6 text-gradient-title uppercase">
            {register.title}
          </h2>
          <p className="font-italic-serif text-[20px] sm:text-[26px] md:text-[32px] lg:text-[36px] mt-2 text-gradient-blue">
            {register.italic}
          </p>
          <p className="text-slate-400 text-[14px] sm:text-[15px] mt-4">{register.intro}</p>
        </div>

        <form
          onSubmit={onSubmit}
          className="card-glow rounded-2xl p-5 sm:p-6 md:p-10 mt-10 sm:mt-12"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <FieldLabel>Full Name</FieldLabel>
              <input
                type="text"
                placeholder="Priya Sharma"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                className="mt-2 w-full px-4 py-3 rounded-lg bg-[#0b1220]/70 border hairline text-white placeholder:text-slate-500 outline-none focus:border-[#5EC7F5]/50 transition-colors"
              />
            </div>
            <div>
              <FieldLabel>Email Address</FieldLabel>
              <input
                type="email"
                placeholder="you@company.com"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                className="mt-2 w-full px-4 py-3 rounded-lg bg-[#0b1220]/70 border hairline text-white placeholder:text-slate-500 outline-none focus:border-[#5EC7F5]/50 transition-colors"
              />
            </div>
            <div>
              <FieldLabel>WhatsApp / Mobile</FieldLabel>
              <input
                type="tel"
                placeholder="+91 98765 43210"
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                className="mt-2 w-full px-4 py-3 rounded-lg bg-[#0b1220]/70 border hairline text-white placeholder:text-slate-500 outline-none focus:border-[#5EC7F5]/50 transition-colors"
              />
              <p className="text-[11px] text-[#5EC7F5]/80 mt-2 tracking-wide">
                We'll send the joining link here too.
              </p>
            </div>
            <div>
              <FieldLabel>City</FieldLabel>
              <input
                type="text"
                placeholder="Bengaluru"
                value={form.city}
                onChange={(e) => set("city", e.target.value)}
                className="mt-2 w-full px-4 py-3 rounded-lg bg-[#0b1220]/70 border hairline text-white placeholder:text-slate-500 outline-none focus:border-[#5EC7F5]/50 transition-colors"
              />
            </div>
          </div>

          <div className="mt-8">
            <FieldLabel>Current Status</FieldLabel>
            <RadioGroup
              options={register.statusOptions}
              value={form.status}
              onChange={(v) => set("status", v)}
            />
          </div>

          <div className="mt-8">
            <FieldLabel>AI Familiarity</FieldLabel>
            <RadioGroup
              options={register.aiOptions}
              value={form.ai}
              onChange={(v) => set("ai", v)}
            />
          </div>

          <div className="mt-8">
            <FieldLabel>Main Reason For Attending</FieldLabel>
            <RadioGroup
              options={register.reasonOptions}
              value={form.reason}
              onChange={(v) => set("reason", v)}
            />
          </div>

          <div className="mt-8">
            <FieldLabel>Interested In Applied AI Programme?</FieldLabel>
            <RadioGroup
              options={register.interestOptions}
              value={form.interest}
              onChange={(v) => set("interest", v)}
            />
          </div>

          <div className="mt-8 flex items-start gap-3">
            <button
              type="button"
              onClick={() => set("agree", !form.agree)}
              className={`mt-1 w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                form.agree ? "border-[#5EC7F5] bg-[#5EC7F5]/20" : "border-slate-500"
              }`}
              aria-label="Agree to communications"
            >
              {form.agree && <Check className="w-3 h-3 text-[#5EC7F5]" />}
            </button>
            <p className="text-[13px] text-slate-400 leading-relaxed">
              I agree to receive webinar details, reminders and relevant follow-up communication from Epsilon Executive Education.
            </p>
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="reserve-seat-submit-btn"
            className="mt-8 w-full flex items-center justify-center gap-2 py-4 rounded-full bg-[#5EC7F5] text-[#062026] font-semibold text-sm glow-blue-btn hover:bg-[#5EC7F5] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                Sending <Loader2 className="w-4 h-4 animate-spin" />
              </>
            ) : (
              <>
                {register.cta} <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
          <p className="text-[12px] text-slate-500 text-center mt-5">
            {register.footnote}
          </p>
        </form>
      </div>
    </section>
  );
};

export default Register;
